// Define the public path
if ((window as any).PublicPath != null)
    __webpack_public_path__ = (window as any).PublicPath;

// Import polyfill
import 'ts-polyfill';
import 'whatwg-fetch';

import * as ReactDOM from "react-dom";
import _ from "lodash";
import { ToastContainer } from "react-toastify";

// Material-UI
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';

// Internal library
import { DebugEx } from '@Utils/DebugEx';
import { Board, GetDefaultDashboardTheme } from '../Dashboard/Board';
import { DashboardTheme } from '@UI/Dashboard/DashboardManager';
import { getMUITheme } from '@Primitive/ThemeProvider';
import { setGlobal } from '@Utils/Globals';
import { isDefined, isFunction, IsNullOrWhiteSpace, RunSafe } from '@Utils/Micro';

// ================================================================================================
// Init phase
// ================================================================================================

export interface DashboardLibSettings {
    /**
     * DOM element's id in which the UI will be rendered
     */
    ElementId: string;

    /**
     * Board's UIDto load.
     */
    BoardId: string;

    /**
     * Board's public key (can be found in board settings)
     */
    PublicKey: string;

    /**
     * Yodiboard server URI (e.g. https://dashboard.yodiwo.com)
     */
    DashboardServerAddress: string;

    /**
     * UI theme
     */
    Theme: DashboardTheme;

    /**
     * Default selected page. Can be left null
     */
    DefaultSelectedDashboardKey?: string;

    /**
     * Whether board will start with open page list or closed.
     */
    DefaultDashboardListStartOpen?: boolean;

    /**
     * (Optional) Callback triggered on successful plegma connection
     */
    onAssignedEndpoint?: (key: string) => void;

    /**
     * (Optional) Callback triggered when error occurs.
     * Possible errors: save/load error, plegma connection failure
     */
    onError?: (msg: string) => void;
}

// ------------------------------------------------------

if ((window as any).Yodiwo_isLoaded == undefined) {
    (window as any).Yodiwo_isLoaded = true;
    DebugEx.LogAppInfo("YodiBoard-Lib");
    var setup = (settings: DashboardLibSettings) => {
        // Theme for Material UI
        let MUI_Theme = getMUITheme();

        // Add global function for dashboard refresh
        setGlobal("YodiBoardServerAddress", settings.DashboardServerAddress);

        // assign default `ElementId`
        if (!isDefined(settings.ElementId))
            settings.ElementId = 'app-mount-point';

        // Use server address
        if (!IsNullOrWhiteSpace(settings.DashboardServerAddress))
            __webpack_public_path__ = settings.DashboardServerAddress + "/";

        settings.Theme = _.merge(
            GetDefaultDashboardTheme(),
            {
                Loading: {
                    LoadingImageURL: "https://dashboard.yodiwo.com/Content/img/Emerald-icon.png",
                }
            },
            settings.Theme);

        // rendering function for the main application that is replace the mount-point
        let mainAppRender = () => {
            let element = document.getElementById(settings.ElementId);
            let rect = element.getBoundingClientRect();
            return ReactDOM.render(
                <MuiThemeProvider muiTheme={MUI_Theme}>
                    <ToastContainer
                        position={'top-right'}
                        hideProgressBar={false}
                        pauseOnHover={false}
                        closeButton={false}
                        closeOnClick
                        draggable
                        pauseOnFocusLoss
                        newestOnTop
                        style={{
                            paddingBottom: 8
                        }}
                    />
                    <Board
                        width={rect.width}
                        height={rect.height}
                        UserInfo={null}
                        IsEditable={false}
                        LoadingYodiBoardId={null}
                        LoadingPublicBoard={{
                            BoardId: settings.BoardId,
                            PublicKey: settings.PublicKey
                        }}
                        DomainInfo={null}
                        SelectedPageId={settings.DefaultSelectedDashboardKey}
                        DefaultIsListOpen={settings.DefaultDashboardListStartOpen}
                        Theme={settings.Theme}
                        onAssignedEndpoint={(key) => {
                            if (isFunction(settings.onAssignedEndpoint))
                                RunSafe(() => settings.onAssignedEndpoint(key));
                        }}
                        ServersInfo={null}
                        onError={(msg: string) => {
                            if (isFunction(settings.onError))
                                RunSafe(() => settings.onError(msg));
                        }}
                    />
                </MuiThemeProvider>,
                element);
        };

        // If the browser resized redraw everything
        let element = document.getElementById(settings.ElementId);
        element.addEventListener('resize', mainAppRender);
        window.addEventListener('orientationchange', mainAppRender);

        // Add global function for dashboard refresh
        setGlobal("YodiBoardRefresh", mainAppRender);

        // First rendering of the main application
        mainAppRender();
    }
    setGlobal("YodiBoardSetup", setup);
}

// ------------------------------------------------------